import * as React from 'react';
import { Box, Text, VStack, HStack, Icon, Button, Stack, Field } from '@chakra-ui/react';
import { FiUpload, FiFile, FiX, FiCheck } from 'react-icons/fi';

export interface FileUploadFile {
  file: File;
  id: string;
  progress?: number;
  error?: string;
  uploaded?: boolean;
}

export interface FileUploadProps {
  label?: string;
  error?: string;
  hint?: string;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number; // in MB
  disabled?: boolean;
  required?: boolean;
  containerClassName?: string;
  onFilesChange?: (files: FileUploadFile[]) => void;
  onUpload?: (file: File) => Promise<void>;
}

const FileUpload = React.forwardRef<HTMLInputElement, FileUploadProps>(
  (
    {
      label,
      error,
      hint,
      accept,
      multiple = false,
      maxFiles = 5,
      maxSize = 10, // 10MB default
      disabled,
      required,
      containerClassName,
      onFilesChange,
      onUpload,
      ...props
    },
    _ref
  ) => {
    const [files, setFiles] = React.useState<FileUploadFile[]>([]);
    const [dragActive, setDragActive] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Determine field status
    const invalid = !!error;

    // Handle file selection
    const handleFiles = React.useCallback(
      async (newFiles: FileList | null) => {
        if (!newFiles) return;

        const fileArray = Array.from(newFiles);
        const maxSizeBytes = maxSize * 1024 * 1024;

        // Validate and process files
        const processedFiles: FileUploadFile[] = fileArray
          .slice(0, multiple ? maxFiles : 1)
          .filter((file) => file.size <= maxSizeBytes)
          .map((file) => ({
            file,
            id: `${file.name}-${Date.now()}-${Math.random()}`,
            progress: 0,
            uploaded: false,
          }));

        // Update state
        const updatedFiles = multiple
          ? [...files, ...processedFiles].slice(0, maxFiles)
          : processedFiles;

        setFiles(updatedFiles);
        onFilesChange?.(updatedFiles);

        // Upload files if upload handler provided
        if (onUpload) {
          for (const fileItem of processedFiles) {
            try {
              await onUpload(fileItem.file);
              setFiles((prev) =>
                prev.map((f) =>
                  f.id === fileItem.id ? { ...f, uploaded: true, progress: 100 } : f
                )
              );
            } catch {
              setFiles((prev) =>
                prev.map((f) => (f.id === fileItem.id ? { ...f, error: 'Upload failed' } : f))
              );
            }
          }
        }
      },
      [files, maxFiles, maxSize, multiple, onFilesChange, onUpload]
    );

    // Handle drag events
    const handleDrag = React.useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === 'dragenter' || e.type === 'dragover') {
        setDragActive(true);
      } else if (e.type === 'dragleave') {
        setDragActive(false);
      }
    }, []);

    const handleDrop = React.useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
          handleFiles(e.dataTransfer.files);
        }
      },
      [handleFiles]
    );

    // Handle file input change
    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
          handleFiles(e.target.files);
        }
      },
      [handleFiles]
    );

    // Remove file
    const removeFile = React.useCallback(
      (id: string) => {
        const updatedFiles = files.filter((f) => f.id !== id);
        setFiles(updatedFiles);
        onFilesChange?.(updatedFiles);
      },
      [files, onFilesChange]
    );

    // Format file size
    const formatFileSize = (bytes: number) => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
      <Field.Root
        {...(invalid ? { invalid: true } : {})}
        className={containerClassName}
        disabled={disabled}
      >
        {/* Label */}
        {label && (
          <Box as="label" fontSize="sm" fontWeight="medium" color={invalid ? '#EF4444' : '#374151'}>
            {label}
            {required && (
              <Box as="span" color="#EF4444" marginLeft={1}>
                *
              </Box>
            )}
          </Box>
        )}

        {/* Drop Zone */}
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          position="relative"
          border="2px dashed"
          borderColor={dragActive ? 'blue.500' : invalid ? 'red.500' : 'gray.300'}
          borderRadius="lg"
          padding={6}
          textAlign="center"
          cursor={disabled ? 'not-allowed' : 'pointer'}
          backgroundColor={dragActive ? '#EBF8FF' : disabled ? '#F9FAFB' : 'white'}
          transition="all 0.2s"
          _hover={!disabled ? { borderColor: '#60A5FA', backgroundColor: '#F9FAFB' } : {}}
        >
          <input
            ref={inputRef}
            type="file"
            multiple={multiple}
            accept={accept}
            onChange={handleChange}
            disabled={disabled}
            hidden
            {...props}
          />

          <VStack gap={2}>
            <Icon>
              <FiUpload size="24" />
            </Icon>
            <Text fontSize="sm" fontWeight="medium">
              {dragActive ? 'Drop files here' : 'Click to upload or drag and drop'}
            </Text>
            <Text fontSize="xs" color="#9CA3AF">
              {accept ? `Accepted: ${accept}` : 'All file types accepted'}
              {' • '}
              Max {maxSize}MB per file
              {multiple && ` • Max ${maxFiles} files`}
            </Text>
          </VStack>
        </Box>

        {/* File List */}
        {files.length > 0 && (
          <Stack gap={2} marginTop={3}>
            {files.map((fileItem) => (
              <Box
                key={fileItem.id}
                padding={3}
                border="1px solid"
                borderColor={fileItem.error ? 'red.300' : 'gray.200'}
                borderRadius="md"
                backgroundColor={fileItem.error ? 'red.50' : 'gray.50'}
              >
                <HStack justify="space-between" align="start">
                  <HStack flex={1} gap={3}>
                    <Icon color={fileItem.error ? 'red.500' : 'gray.500'}>
                      <FiFile />
                    </Icon>
                    <VStack align="start" gap={0} flex={1}>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        maxWidth="200px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {fileItem.file.name}
                      </Text>
                      <Text fontSize="xs" color="#9CA3AF">
                        {formatFileSize(fileItem.file.size)}
                      </Text>
                      {fileItem.error && (
                        <Text fontSize="xs" color="#EF4444">
                          {fileItem.error}
                        </Text>
                      )}
                    </VStack>
                  </HStack>

                  <HStack>
                    {fileItem.uploaded && (
                      <Icon color="#22C55E">
                        <FiCheck />
                      </Icon>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        removeFile(fileItem.id);
                      }}
                      aria-label="Remove file"
                    >
                      <Icon>
                        <FiX />
                      </Icon>
                    </Button>
                  </HStack>
                </HStack>

                {fileItem.progress !== undefined &&
                  fileItem.progress > 0 &&
                  fileItem.progress < 100 && (
                    <Box
                      height="4px"
                      width="full"
                      backgroundColor="gray.200"
                      borderRadius="full"
                      marginTop={2}
                    >
                      <Box
                        height="full"
                        width={`${fileItem.progress}%`}
                        backgroundColor="blue.500"
                        borderRadius="full"
                        transition="width 0.3s ease"
                      />
                    </Box>
                  )}
              </Box>
            ))}
          </Stack>
        )}

        {/* Error Message */}
        {error && (
          <Box fontSize="sm" color="#EF4444" marginTop={1}>
            {error}
          </Box>
        )}

        {/* Hint Message */}
        {hint && !error && (
          <Box fontSize="sm" color="#6B7280" marginTop={1}>
            {hint}
          </Box>
        )}
      </Field.Root>
    );
  }
);

FileUpload.displayName = 'FileUpload';

export { FileUpload };
